import { FeatureToggles } from '@/features/feature-toggles'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { observable, action, computed } from 'mobx'
import { EMPTY, from, type Observable } from 'rxjs'
import { type Member } from '@/features/iam/members/Member'
import { catchError, filter, switchMap } from 'rxjs/operators'
import {
  defaultDocumentsListView,
  type DocumentsListView,
  DocumentsListViewModel,
} from '@/features/documents/DocumentsListViewModel'
import { type DocumentStore } from '@/features/documents/DocumentStore'
import type { AlertFactory, AlertRetryWrapperFactory } from '@/features/shell'
import { task } from 'mobx-task'
import { UploadBarViewModel } from '@/features/documents/UploadBarViewModel'
import { Id } from '@/infra/id'
import { orderBy } from 'lodash-es'
import type {
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceSelectionFn,
} from '@/features/iam'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import { type Document } from '@/features/documents/Document'
import type { InternalCreateDocumentViewModelFn } from '@/features/documents/internal'
import { tapAction, toStream } from '@/utils/mobx-util'

/**
 * Workspace filter for Documents.
 */
export type DocumentsWorkspaceFilter = AllWorkspaces | SpecificWorkspace

/**
 * Documents in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * Documents in a specific workspace.
 */
export interface SpecificWorkspace {
  type: 'SpecificWorkspace'
  workspace: Workspace
}

/**
 * What the Documents Center shows for a workspace.
 */
interface DocumentsView {
  workspaceFilter: DocumentsWorkspaceFilter
  list: DocumentsListView
}

/**
 * View model for the Documents Center.
 */
export class DocumentRootViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private _syncedMember: Member | null = null

  /**
   * Each workspace keeps its Documents Center view for the rest of the
   * session.
   */
  private readonly _workspaceViews = new Map<Id, DocumentsView>()

  @observable accessor workspaceFilter: DocumentsWorkspaceFilter = {
    type: 'AllWorkspaces',
  }

  readonly documentsListVm

  /**
   * Pages through the documents in the workspace selected for filtering.
   */
  readonly scrollProvider = new InfiniteScrollProvider<Document>({
    limit: 30,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading documents failed',
        signal,
      )

      return withRetry(() =>
        this._documentStore.searchDocuments(
          {
            context: 'User',
            workspaceId: this.workspaceForFiltering?.id,
            ...paging,
          },
          signal,
        ),
      )
    },
  })

  readonly uploadBarVm

  constructor(
    createDocumentViewModel: InternalCreateDocumentViewModelFn,
    currentMember$: Observable<Member>,
    private readonly _getWorkspaceSelection: GetWorkspaceSelectionFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _documentStore: DocumentStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this.uploadBarVm = new UploadBarViewModel(
      this._newAlert,
      this._documentStore,
    )

    this.documentsListVm = new DocumentsListViewModel(
      () => this.documents,
      () => this.workspaceForFiltering,
      this.uploadBarVm,
      createDocumentViewModel,
    )

    // Syncs the workspace filter when the current member changes.
    // For documents we default to the current workspace view in order to prevent
    // accidentally modifying documents in another workspace when coming
    // from another workspace
    this._lifecycle.manage(
      currentMember$.pipe(
        // `currentMember$` replays on every activation, which must not
        // undo a filter the user picked for the same member.
        filter((member) => member !== this._syncedMember),
        tapAction((member) => this._syncWorkspaceView(member)),
      ),
    )

    // Registered after the member sync so that a filter it picks on
    // activation is loaded once.
    this._lifecycle.manage(
      toStream(() => this.workspaceFilter, true).pipe(
        // The first value is the refetch on activation, which keeps the
        // documents of the previous visit on screen. A new filter must not.
        switchMap((_, index) =>
          from(this.fetch(this._lifecycle.signal, index === 0)).pipe(
            // The view renders a failed load from the task's own state.
            catchError(() => EMPTY),
          ),
        ),
      ),
    )

    this._lifecycle.manage(
      syncInfiniteScrollerItems({
        scroller: this.scrollProvider,
        additions$: this._documentStore.documentAdded$,
        removals$: this._documentStore.documentRemoved$,
      }),
    )

    this._lifecycle.manage(this.uploadBarVm)
    this._lifecycle.manage(this.documentsListVm)
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @computed
  get currentWorkspace() {
    return this._getCurrentMember().workspace
  }

  /**
   * The current user's loaded documents.
   */
  @computed
  get documents() {
    const user = this._getCurrentMember().user
    return this.scrollProvider.items.filter(
      (doc) => doc.owner.user === user && !doc.archived && !doc.ephemeral,
    )
  }

  @computed
  get isWorkspaceSelected() {
    return !!this.workspaceForFiltering
  }

  /**
   * The v5 API requires uploads to be linked to a job, so uploads
   * from the Documents Center are hidden while it's enabled.
   */
  @computed
  get canUploadFiles() {
    return !FeatureToggles.documentsApiV5()
  }

  /**
   * Current user workspaces.
   */
  @computed
  get currentUserWorkspaces() {
    return orderBy(
      this._getCurrentUserWorkspaceMemberships().map((m) => m.workspace),
      (ws) => ws.name.toLowerCase(),
      'asc',
    )
  }

  /**
   * The workspace selected for filtering, `Workspace.none` for all
   * workspaces.
   */
  @computed
  get selectedWorkspace() {
    return this.workspaceForFiltering ?? Workspace.none
  }

  @computed
  get workspaceForFiltering() {
    return this.workspaceFilter.type === 'SpecificWorkspace'
      ? this.workspaceFilter.workspace
      : undefined
  }

  /**
   * Filters by the current member's workspace.
   */
  @action.bound
  selectCurrentWorkspaceForFiltering() {
    this.selectWorkspaceForFiltering(this.currentWorkspace)
  }

  /**
   * Uploads picked or dropped files, unless an upload is underway.
   */
  @action.bound
  queueFilesUpload(files: FileList) {
    if (!this.uploadBarVm.uploading) {
      void this.uploadFiles(files)
    }
  }

  @action.bound
  dragEnter() {
    if (this.canUploadFiles) {
      this.uploadBarVm.dragEnter()
    }
  }

  /**
   * Upload files to the currently selected workspace
   * @param files
   */
  async uploadFiles(files: FileList) {
    if (!this.canUploadFiles || files.length < 1) {
      return
    }

    if (!this.workspaceForFiltering) {
      return
    }

    const workspaceId = Id.assert(this.workspaceForFiltering.id)

    const filesToUpload = Array.from(files).map((f) => ({
      file: f,
      workspaceId,
    }))

    await this.uploadBarVm.uploadFiles(filesToUpload)
  }

  @computed
  get workspaceDocs() {
    const workspace = this.workspaceForFiltering
    if (!workspace) {
      return this.documents
    }

    return this.documents.filter((doc) => doc.workspace === workspace)
  }

  /**
   * Whether the documents are refetching while the previous ones stay on
   * screen.
   */
  @computed
  get isRevalidating() {
    return this.scrollProvider.isRevalidating
  }

  @computed
  get hasDocuments() {
    return this.workspaceDocs.length > 0
  }

  /**
   * How many documents to show in the heading, if any.
   */
  @computed
  get documentsCount() {
    return this.hasDocuments ? this.workspaceDocs.length : undefined
  }

  /**
   * Whether files can be uploaded from the documents list, which requires
   * a workspace to upload to.
   */
  @computed
  get canUploadFilesToWorkspace() {
    return this.isWorkspaceSelected && this.canUploadFiles
  }

  @computed
  get isWorkspaceEmpty() {
    return this.isWorkspaceSelected && !this.hasDocuments
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'SpecificWorkspace' }
  }

  /**
   * Switches to the view of the member's workspace, and when coming from
   * another workspace or all workspaces, filters by the member's workspace.
   */
  private _syncWorkspaceView(member: Member) {
    const previous = this._syncedMember
    this._syncedMember = member

    const workspaceId = Id.assert(member.workspace.id)
    if (previous && previous.workspace.id !== member.workspace.id) {
      this._workspaceViews.set(Id.assert(previous.workspace.id), {
        workspaceFilter: this.workspaceFilter,
        list: this.documentsListVm.view,
      })

      const saved = this._workspaceViews.get(workspaceId)
      if (saved) {
        this.workspaceFilter = saved.workspaceFilter
        this.documentsListVm.restoreView(saved.list)
        return
      }

      this.workspaceFilter = { type: 'AllWorkspaces' }
      this.documentsListVm.restoreView(defaultDocumentsListView)
    }

    // A user who picked "All workspaces" keeps seeing all of them.
    if (this._getWorkspaceSelection().allWorkspaces) {
      return
    }

    if (
      this.workspaceFilter.type === 'AllWorkspaces' ||
      this.workspaceFilter.workspace !== member.workspace
    ) {
      this.selectWorkspaceForFiltering(member.workspace)
    }
  }

  /**
   * Loads the first page of documents in the workspace selected for
   * filtering.
   */
  fetch = task(async (signal: AbortSignal, staleWhileRevalidate?: boolean) => {
    await this.scrollProvider.fetch({ signal, staleWhileRevalidate })
  })
}
